/**
 * Composable for contract status (start + end date) and styling
 * Provides helpers to determine employee contract status and colors
 */
export function useContractStatus() {
  /**
   * Contract status types
   */
  const STATUS = {
    ACTIVE: 'active', // No end date or end date > 30 days away
    ENDING_SOON: 'ending_soon', // End date within 30 days
    ENDING_VERY_SOON: 'ending_very_soon', // End date within 7 days
    ENDED: 'ended', // End date in the past
    NOT_YET_STARTED: 'not_yet_started' // Start date in the future
  }

  /**
   * Get contract status for an employee
   * @param {string|Date|null} contractEndDate - The contract end date
   * @param {string|Date|null} contractStartDate - The contract start date
   * @returns {string} One of STATUS values
   */
  function getContractStatus(contractEndDate, contractStartDate) {
    // Check start date first: if in the future, employee hasn't started yet
    if (contractStartDate) {
      const startDate = new Date(contractStartDate)
      const today = new Date()

      if (toLocalMidnightUTC(startDate) > toLocalMidnightUTC(today)) return STATUS.NOT_YET_STARTED
    }

    if (!contractEndDate) return STATUS.ACTIVE

    const diffDays = getDaysUntilEnd(contractEndDate)

    if (diffDays < 0) return STATUS.ENDED
    if (diffDays <= 7) return STATUS.ENDING_VERY_SOON
    if (diffDays <= 30) return STATUS.ENDING_SOON
    return STATUS.ACTIVE
  }

  /**
   * Convert a Date to a UTC timestamp representing its local calendar date at midnight.
   * Uses Date.UTC with local date components to avoid DST issues in day arithmetic.
   */
  function toLocalMidnightUTC(date) {
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  }

  /**
   * Get days until contract end (negative if already ended)
   * @param {string|Date|null} contractEndDate
   * @returns {number|null} Days until end, null if no end date
   */
  function getDaysUntilEnd(contractEndDate) {
    if (!contractEndDate) return null

    const endDate = new Date(contractEndDate)
    const today = new Date()

    return Math.round(
      (toLocalMidnightUTC(endDate) - toLocalMidnightUTC(today)) / (1000 * 60 * 60 * 24)
    )
  }

  /**
   * Get status dot color class (Tailwind)
   * @param {string|Date|null} contractEndDate
   * @param {string|Date|null} contractStartDate
   * @returns {string} Tailwind color class for the status dot
   */
  function getStatusDotColor(contractEndDate, contractStartDate) {
    const status = getContractStatus(contractEndDate, contractStartDate)

    switch (status) {
      case STATUS.NOT_YET_STARTED:
        return 'bg-orange-500 dark:bg-orange-400'
      case STATUS.ENDED:
        return 'bg-gray-400 dark:bg-gray-500'
      case STATUS.ENDING_VERY_SOON:
        return 'bg-red-500 dark:bg-red-400'
      case STATUS.ENDING_SOON:
        return 'bg-orange-500 dark:bg-orange-400'
      default:
        return 'bg-green-500 dark:bg-green-400'
    }
  }

  /**
   * Get text color class based on contract status (Tailwind)
   * @param {string|Date|null} contractEndDate
   * @param {string|Date|null} contractStartDate
   * @returns {string} Tailwind text color class
   */
  function getStatusTextColor(contractEndDate, contractStartDate) {
    const status = getContractStatus(contractEndDate, contractStartDate)

    switch (status) {
      case STATUS.NOT_YET_STARTED:
        return 'text-orange-600 dark:text-orange-400'
      case STATUS.ENDED:
        return 'text-gray-500 dark:text-gray-400'
      case STATUS.ENDING_VERY_SOON:
        return 'text-red-600 dark:text-red-400'
      case STATUS.ENDING_SOON:
        return 'text-orange-600 dark:text-orange-400'
      default:
        return 'text-gray-600 dark:text-gray-400'
    }
  }

  /**
   * Format a date for display
   * @param {string|Date|null} date
   * @returns {string} Formatted date string
   */
  function formatContractEndDate(date) {
    if (!date) return ''

    const d = new Date(date)
    return d.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  /**
   * Get a human-readable status message
   * @param {string|Date|null} contractEndDate
   * @param {string|Date|null} contractStartDate
   * @returns {string} Status message
   */
  function getStatusMessage(contractEndDate, contractStartDate) {
    const status = getContractStatus(contractEndDate, contractStartDate)
    const days = getDaysUntilEnd(contractEndDate)

    switch (status) {
      case STATUS.NOT_YET_STARTED:
        return `In dienst vanaf: ${formatContractEndDate(contractStartDate)}`
      case STATUS.ENDED:
        return `Uit dienst sinds ${formatContractEndDate(contractEndDate)}`
      case STATUS.ENDING_VERY_SOON:
        return `Uit dienst over ${days} dag${days === 1 ? '' : 'en'}`
      case STATUS.ENDING_SOON:
        return `Uit dienst over ${days} dagen`
      default:
        return ''
    }
  }

  /**
   * Check if employee should show a warning based on contract status
   * @param {string|Date|null} contractEndDate
   * @param {string|Date|null} contractStartDate
   * @returns {boolean}
   */
  function shouldShowWarning(contractEndDate, contractStartDate) {
    const status = getContractStatus(contractEndDate, contractStartDate)
    return status !== STATUS.ACTIVE
  }

  /**
   * Check if a specific week is past the contract end date
   * @param {string|Date} weekStart - Start of the week to check
   * @param {string|Date|null} contractEndDate - The contract end date
   * @returns {boolean} True if the week starts after the end date
   */
  function isWeekPastEndDate(weekStart, contractEndDate) {
    if (!contractEndDate) return false

    const weekStartDate = new Date(weekStart)
    const endDate = new Date(contractEndDate)

    return toLocalMidnightUTC(weekStartDate) > toLocalMidnightUTC(endDate)
  }

  /**
   * Check if project period extends past contract end date
   * @param {string|Date} projectEndDate - Project end date
   * @param {string|Date|null} contractEndDate - The contract end date
   * @returns {boolean} True if project extends past contract
   */
  function projectExtendsPastContract(projectEndDate, contractEndDate) {
    if (!contractEndDate || !projectEndDate) return false

    const projEnd = new Date(projectEndDate)
    const contractEnd = new Date(contractEndDate)

    return toLocalMidnightUTC(projEnd) > toLocalMidnightUTC(contractEnd)
  }

  return {
    // Constants
    STATUS,

    // Status functions
    getContractStatus,
    getDaysUntilEnd,
    shouldShowWarning,

    // Styling functions
    getStatusDotColor,
    getStatusTextColor,

    // Formatting functions
    formatContractEndDate,
    getStatusMessage,

    // Week/project checks
    isWeekPastEndDate,
    projectExtendsPastContract
  }
}
